import axios, { Method } from 'axios';
import { Executable, IPrepareRequestOptions } from '../executable';
import {
  IWalletTemplateCreateInput,
  IWalletTemplatePassData,
  IWalletTemplateAndroidPassData,
  IWalletTemplateFieldMapping,
  BambuPassStyles,
} from '../interfaces/wallet-template';
import { UploadTemporaryFile } from './upload-temporary-file';

// had to make some hack to make the module be imported
let fileTypeModule: any;

/**
 * The IWalletTemplateCreateResponse returns the templateId and possibly a message after creation.
 */
export interface IWalletTemplateCreateResponse {
  templateId: number;
  message: string;
}

/**
 * The IWalletTemplateCreateExtendedInput collects the Wallet Template details and the brandId and programIds used to construct the wallet template.
 */
export interface IWalletTemplateCreateExtendedInput extends IWalletTemplateCreateInput {
  brandId: number;
  programId: number;
}
/**
 * CreateWalletTemplate is the executable class for creating a wallet template.
 * @param input IWalletTemplateCreateExtendedInput
 * @returns The templateId and possibly a message after creation.
 */
export class CreateWalletTemplate
  extends Executable<IWalletTemplateCreateResponse>
  implements Partial<IWalletTemplateCreateExtendedInput>
{
  public brandId?: number;

  public programId?: number;

  public name?: string;

  public organizationName?: string;

  public sharingProhibited?: boolean;

  public sharingUrl?: string;

  public passdata?: IWalletTemplatePassData;

  public androidPassData?: IWalletTemplateAndroidPassData;

  public fieldMappings?: IWalletTemplateFieldMapping[];

  public walletIdentifierId?: number;

  public defaultMessage?: string;

  public getMethod(): Method {
    return 'post';
  }

  public async prepareRequest({ execute }: IPrepareRequestOptions): Promise<any> {
    if (!fileTypeModule) {
      fileTypeModule = await import('file-type');
    }

    const appleImages = this?.passdata?.images || [];
    const androidImages = this?.androidPassData?.images || [];
    const allImagesRaw = [...appleImages, ...androidImages];
    const allImages = [] as {
      image: string;
      imageFile: Buffer;
      contentType: string;
      type: 'apple' | 'android';
    }[];

    for (let i = 0; i < allImagesRaw.length; i++) {
      const image = allImagesRaw[i];
      const imageBuffer =
        image.imageFile instanceof Buffer
          ? image.imageFile
          : Buffer.from(image.imageFile, 'base64');
      const filetype = await fileTypeModule.fileTypeFromBuffer(imageBuffer);

      if (filetype) {
        allImages.push({
          image: image.image,
          imageFile: imageBuffer,
          contentType: filetype.mime,
          type: i < appleImages.length ? 'apple' : 'android',
        });
      } else {
        throw new Error(
          `Could not extract image type from ${
            i < appleImages.length ? 'apple' : 'android'
          } image "${image.image}"`
        );
      }
    }

    const uploadLinks = await execute(
      new UploadTemporaryFile({
        brandId: this.brandId,
        amount: allImages.length,
      })
    );

    const uploadMap = {} as { [key: string]: string };

    for (let i = 0; i < allImages.length; i++) {
      const { uploadUrl, key } = uploadLinks[i];
      await axios.put(uploadUrl, allImages[i].imageFile, {
        headers: {
          'Content-Type': allImages[i].contentType,
        },
      });
      uploadMap[`${allImages[i].type}_${allImages[i].image}`] = key;
    }

    for (const image of allImages) {
      if (image.type === 'android' && this.androidPassData?.images) {
        const originalImage = this.androidPassData.images.find((i) => i.image === image.image);
        if (originalImage) {
          originalImage.imageFile = uploadMap[`android_${image.image}`];
        }
      } else {
        const originalImage = this.passdata?.images.find((i) => i.image === image.image);
        if (originalImage) {
          originalImage.imageFile = uploadMap[`apple_${image.image}`];
        }
      }
    }
  }

  public getResponse(apiResponse: any): IWalletTemplateCreateResponse {
    return apiResponse;
  }

  public getURLPath(tenantId?: string): string {
    return `${this.brandId}/programs/${this.programId}/templates`;
  }

  constructor(
    input?: Partial<IWalletTemplateCreateExtendedInput> & { tenantId: number; name: string }
  ) {
    super();
    if (input) {
      Object.assign(this, input);
    }
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): Partial<IWalletTemplateCreateExtendedInput> {
    return {
      name: this.name,
      organizationName: this.organizationName,
      sharingProhibited: this.sharingProhibited,
      sharingUrl: this.sharingUrl,
      passdata: this.passdata,
      androidPassData: this.androidPassData,
      fieldMappings: this.fieldMappings,
      walletIdentifierId: this.walletIdentifierId,
      defaultMessage: this.defaultMessage,
    };
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return 'brands';
  }
}

export { BambuPassStyles };
